import { Action } from '@ngrx/store';
import { type } from '../util';
import { PasswordRecoveryManagement } from '../../models/password-recovery-management.model';
import { TechnicalError } from '../../models/errors.model';

/**
 * For each action type in an action group, make a simple
 * enum object for all of this group's action types.
 *
 * The 'type' utility function coerces strings into string
 * literal types and runs a simple check to guarantee all
 * action types in the application are unique.
 */
export const ActionTypes = {
  FIND_RECOVERY_METHODS: type('[RecoveryMethods] Find'),
  FIND_RECOVERY_METHODS_FAIL: type('[RecoveryMethods] Find Failed'),
  FOUND_RECOVERY_METHODS: type('[RecoveryMethods] Found'),
  SUBMIT_EMAIL_RECOVERY_METHOD: type('[EmailRecoveryMethod] Submit'),
  SET_EMAIL_RECOVERY_METHOD: type('[EmailRecoveryMethod] Update'),
  SET_EMAIL_RECOVERY_METHOD_SUCCESS: type(
    '[EmailRecoveryMethod] Update Success',
  ),
  SET_EMAIL_RECOVERY_METHOD_FAIL: type('[EmailRecoveryMethod] Update Failure'),
  SET_SMS_RECOVERY_METHOD: type('[SmsRecoveryMethod] Update'),
  SET_SMS_RECOVERY_METHOD_SUCCESS: type('[SmsRecoveryMethod] Update Success'),
  SET_SMS_RECOVERY_METHOD_FAIL: type('[SmsRecoveryMethod] Update Failure'),
  ACTIVATE_SMS_RECOVERY_METHOD: type('[ActivateSmsRecoveryMethod] Update'),
  ACTIVATE_SMS_RECOVERY_METHOD_SUCCESS: type(
    '[ActivateSmsRecoveryMethod] Update Success',
  ),
  ACTIVATE_SMS_RECOVERY_METHOD_FAIL: type(
    '[ActivateSmsRecoveryMethod] Update Failure',
  ),
  RESET_SUCCESS: type('[RecoveryMethods] Reset Success'),
  RESET_ERROR: type('[RecoveryMethods] Reset Error'),
};

/**
 * Every action is comprised of at least a type and an optional
 * payload. Expressing actions as classes enables powerful
 * type checking in reducer functions.
 *
 * See Discriminated Unions: https://www.typescriptlang.org/docs/handbook/advanced-types.html#discriminated-unions
 */
export class FindRecoveryMethodsAction implements Action {
  type = ActionTypes.FIND_RECOVERY_METHODS;

  constructor(public payload?: any) {}
}

export class FindRecoveryMethodsFailedAction implements Action {
  type = ActionTypes.FIND_RECOVERY_METHODS_FAIL;

  constructor(public payload: TechnicalError) {}
}

export class FoundRecoveryMethodsAction implements Action {
  type = ActionTypes.FOUND_RECOVERY_METHODS;

  constructor(public payload: PasswordRecoveryManagement) {}
}

export class SubmitEmailRecoveryMethodAction implements Action {
  type = ActionTypes.SUBMIT_EMAIL_RECOVERY_METHOD;

  constructor(public payload: string) {}
}

export class SetEmailRecoveryMethodAction implements Action {
  type = ActionTypes.SET_EMAIL_RECOVERY_METHOD;

  constructor(public payload: string) {}
}

export class SetEmailRecoveryMethodSuccessAction implements Action {
  type = ActionTypes.SET_EMAIL_RECOVERY_METHOD_SUCCESS;

  constructor(public payload: string) {}
}

export class SetEmailRecoveryMethodFailAction implements Action {
  type = ActionTypes.SET_EMAIL_RECOVERY_METHOD_FAIL;

  constructor(public payload: TechnicalError) {}
}

export class SetSmsRecoveryMethodAction implements Action {
  type = ActionTypes.SET_SMS_RECOVERY_METHOD;

  constructor(public payload: string) {}
}

export class SetSmsRecoveryMethodSuccessAction implements Action {
  type = ActionTypes.SET_SMS_RECOVERY_METHOD_SUCCESS;

  constructor(public payload: string) {}
}

export class SetSmsRecoveryMethodFailAction implements Action {
  type = ActionTypes.SET_SMS_RECOVERY_METHOD_FAIL;

  constructor(public payload: TechnicalError) {}
}

export class ActivateSmsRecoveryMethodAction implements Action {
  type = ActionTypes.ACTIVATE_SMS_RECOVERY_METHOD;

  constructor(public payload: string) {}
}

export class ActivateSmsRecoveryMethodSuccessAction implements Action {
  type = ActionTypes.ACTIVATE_SMS_RECOVERY_METHOD_SUCCESS;

  constructor(public payload: string) {}
}

export class ActivateSmsRecoveryMethodFailAction implements Action {
  type = ActionTypes.ACTIVATE_SMS_RECOVERY_METHOD_FAIL;

  constructor(public payload: TechnicalError) {}
}

export class ResetError implements Action {
  type = ActionTypes.RESET_ERROR;
  payload = null;
}

export class ResetSuccess implements Action {
  type = ActionTypes.RESET_SUCCESS;
  payload = null;
}

/**
 * Export a type recovery of all actions in this action group
 * so that reducers can easily compose action types
 */
export type Actions =
  | FindRecoveryMethodsAction
  | FoundRecoveryMethodsAction
  | FindRecoveryMethodsFailedAction
  | SubmitEmailRecoveryMethodAction
  | SetEmailRecoveryMethodAction
  | SetEmailRecoveryMethodSuccessAction
  | SetEmailRecoveryMethodFailAction
  | SetSmsRecoveryMethodAction
  | SetSmsRecoveryMethodSuccessAction
  | SetSmsRecoveryMethodFailAction
  | ActivateSmsRecoveryMethodAction
  | ActivateSmsRecoveryMethodSuccessAction
  | ActivateSmsRecoveryMethodFailAction
  | ResetError
  | ResetSuccess;
